import {_, React, observer, TimeAgo, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, pluralize, Fragment, Popover2} from '@api'
import {Icon} from '@blueprintjs/core'
import {Tooltip} from '@material-ui/core'
import {BuilderCard, ChangeLogOperationTypes, NUMBER_OF_CHANGE_LOG_PALETTES, useVipLocalStore, utils, VipLocalStore} from '@modules'
import {ICatalogFieldConfig, StoresInstance, Workspace} from '@stores'
import {MyImage} from 'centric-ui-components'
import {autorun, observable, runInAction, toJS, when} from 'mobx'
import moment from 'moment'
import {ChangeLogEntriesForCatalogItemAtTimestampStore, useChangeLogEntriesForCatalogItemAtTimestampStore, ChangeLogEntryByField} from '.'
import * as css from './ChangeLogEntryIndividual.css'

/// region Store

export class ChangeLogEntryIndividualStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { parent: ChangeLogEntriesForCatalogItemAtTimestampStore }) {
    super(stores)
  }

  get parent() { return this.props.parent}

  get root() { return this.parent.root}

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {fieldLookupKey, root: {queryVariables}} = this
        runInAction(() => {
          const {root: {attributePalettes}} = this
          if (!_.has(attributePalettes, fieldLookupKey)) {
            attributePalettes[fieldLookupKey] = (_.size(attributePalettes) + 1) % NUMBER_OF_CHANGE_LOG_PALETTES
          }
        })
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get workspace() { return this.parent.workspace}

  get item() {
    return this.props.item
  }

  get changed() { return this.item.changed}

  @computed get section() {
    const {changed, workspace: {catalog: {sectionById}}} = this

    return sectionById[changed.field]
  }

  get log() {
    return this.item.item._log
  }

  @computed get operation() {
    const {changed} = this
    return ChangeLogOperationTypes[changed.op]
  }

  @computed get dataMap() {
    const {workspace} = this
    if (!workspace) {
      return null
    }
    return workspace.dataMap
  }

  @computed get field(): ICatalogFieldConfig {
    if (!this.workspace) {
      return null
    }
    const {workspace: {catalog: {_fieldByName}}, changed} = this
    return _fieldByName[changed.field]
  }

  @computed get isImageField() {
    const {field} = this
    return field?.field === 'images'
  }

  @computed get nested() { return this.changed.nested }

  @computed get label() { return this.item.changed.label}

  @computed get fieldLabel() {
    if (!this.workspace) {
      return null
    }
    const {field, nested, label, workspace: {catalog}} = this
    if (field) {
      const fieldLabel = catalog.labelForFieldName_currentCatalog(field.field)
      return nested ? `${label} ${nested}'s ${fieldLabel}` : fieldLabel
    }
    return null
  }

  @observable.ref _beforeValueFromDb
  @observable.ref _afterValueFromDb

  @computed get afterRaw() {
    const {item, field, es, delta, operation, changed: {diff}, strings, _afterValueFromDb} = this
    if (field?.esIndex) {
      let r = es.readField(item, field?.field, field.group?.labelField ?? field.group?.field).valAsString
      if (r) return r
      if (_.isString(diff.after) && diff.after.length > 0) {
        // Get the value from the db
        if (_afterValueFromDb === undefined) {
          field.def?.type !== 'RefList' && setTimeout(async () => {
            const {es} = this
            try {
              this._afterValueFromDb = await es.get({
                index: field.esIndex,
                id: diff.after
              }, {log: {query: false}})
            } catch (err) {
              this._afterValueFromDb = null
              console.log(err, {field, diff, item, delta, operation})
              // debugger
            }
          }, 5)
        }
        return _afterValueFromDb
      } else {
        return null
      }
    } else {
      return _.isString(diff.after)
             ? diff.after
             : JSON.stringify(diff.after)
    }
  }

  @computed get after() {
    const {processValue, afterRaw} = this

    return processValue(afterRaw)
  }

  @computed get beforeRaw() {
    const {item, field, es, delta, operation, changed: {diff}, _beforeValueFromDb} = this
    if (!field) return {}
    if (operation === 'Deleted' && delta !== null) {
      return _.get(_.first(delta[field.field]), 'name')
    } else if (field?.esIndex) {
      let r = es.readField(item, field?.field, field.group?.labelField ?? field.group?.field).valAsString
      if (r) return r
      if (_.isString(diff.before) && diff.before.length > 0) {
        // Get the value from the db
        if (_beforeValueFromDb === undefined) {
          field.def?.type !== 'RefList' && setTimeout(async () => {
            const {es} = this
            try {
              this._beforeValueFromDb = await es.get({
                index: field.esIndex,
                id: diff.before
              }, {log: {query: false}})
            } catch (err) {
              this._beforeValueFromDb = null
              console.log(err, {field, diff, item, delta, operation})
              debugger
            }
          }, 5)
        }
        return _beforeValueFromDb
      } else {
        return null
      }
    } else {
      return _.isString(diff.before)
             ? diff.before
             : JSON.stringify(diff.before)
    }
  }

  processValue = (val: string | any) => {
    const {field, strings} = this

    switch (field?.def?.type) {
      case 'Boolean':
        return _.capitalize(val === 'true' ? (field.def?.booleanLabels?.trueLabel ?? strings.yes) : (field.def?.booleanLabels?.falseLabel ?? strings.no))

      case 'Tag':
        const tag = val ? JSON.parse(val) : null
        return tag?.name ?? strings.emptyLabel

      case 'TagList':
        try {
          const tags = val ? JSON.parse(val) : []
          return tags.map(t => t.name).join(', ')
        }
        catch (err) { return []}

      default:

        if (field?.esIndex) {
          if (field.def?.type === 'RefList') {
          } else {
            if (_.isPlainObject(val)) {
              return _.get(val, field.group?.labelField ?? field.group?.field ?? 'name')
            }

            return val
          }
        }

        return val
    }
  }

  @computed get before() {
    const {processValue, beforeRaw, strings} = this

    return processValue(beforeRaw)
  }

  @computed get showStrikethrough() {
    const { after, field, strings } = this

    return after === _.capitalize(field?.def?.booleanLabels?.falseLabel ?? strings.no)
  }

  @computed get itemName() {
    const {item, dataMap: {title}, root: {config}, stores} = this
    return _.isFunction(config?.title) ? config.title(item, title, stores) : _.get(item, title)
  }

  @action onClickMoveToSeason = (e?: React.MouseEvent) => {
    const {workspace, stores: {flexLayout: {create}}} = this,
      tab = create.newCatalogTab({
        workspace: workspace.config, selectNewTab: true
      })

    setTimeout(() => {
      const {catalogRoot: {catalog}} = tab, {item} = this
      when(() => catalog.hasLoadedConfig, () => {
        catalog.navigateToHierarchy(item)
      })
    }, 2000)
  }

  @computed get imageCountLabel() {
    const {operation, field, before, after, isImageField} = this
    if (!isImageField) {
      return null
    }
    const afterLength = utils.isJson(after) ? JSON.parse(after).length : 0,
      beforeLength = utils.isJson(before) ? JSON.parse(before).length : 0

    switch (operation) {
      case 'Added':
        return `${afterLength} ${pluralize('image', afterLength)}`
      case 'Deleted':
        return `${beforeLength} ${pluralize('image', beforeLength)}`
    }
  }

  @computed get myImages() {
    const {operation, field, before, after, isImageField, catalog} = this
    if (!isImageField) {
      return null
    }
    // console.log({operation, before, after})
    switch (operation) {
      case 'Added':
        return <>{!utils.isJson(after) ? '' : _.map(JSON.parse(after), id => (<MyImage
          width={40}
          height={40}
          key={id}
          className={classNames(css.changedImage)}
          imageUrl={catalog.imageUrlFor({id})}
        />))}</>
      case 'Deleted':
        return <>{!utils.isJson(before) ? '' : _.map(JSON.parse(before), id => (<MyImage
          width={40}
          height={40}
          key={id}
          className={classNames(css.changedImage)}
          imageUrl={catalog.imageUrlFor({id})}
        />))}</>
    }
  }

  //
  // @computed get __html() {
  //   const {operation, fieldLabel, itemName, before, after, onClickItemName, imageCountLabel, myImages, root: {settings: {version}}} = this
  //   switch (operation) {
  //     case 'Added': {
  //       return (
  //         <>
  //           <span className={classNames(css.changeText, imageCountLabel && css.changeTextWithImage)}>
  //             {imageCountLabel ?? <>{version != 'noah' && <Fragment><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;</Fragment>}
  //               <span>&nbsp;{after}</span> to&nbsp;
  //               <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span></>}
  //               </span>
  //           {myImages}
  //         </>
  //       )
  //     }
  //     case 'Changed': {
  //       return (<span className={css.changeText}>
  //         {version != 'noah' && <Fragment><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;</Fragment>}
  //           <span data-type='before-text'>{before}</span>
  //           &nbsp;to {after} in&nbsp;
  //           <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span>
  //       </span>
  //       )
  //     }
  //     case 'Deleted': {
  //       return (
  //         <>
  //           <span className={classNames(css.changeText, imageCountLabel && css.changeTextWithImage)}>
  //             {imageCountLabel ?? <><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;<span data-type='before-text'>{_.isObject(before) ? '' : before}</span></>}
  //             &nbsp;from&nbsp;
  //             <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span>
  //           </span>
  //           {myImages}
  //         </>
  //       )
  //     }
  //   }
  //   return null
  // }
  //
  @computed get delta() {
    return JSON.parse(this.log.delta)
  }

  get id() { return this.item.item.exId}

  @computed get checked() {
    const {root: {_selected}, id} = this
    return _selected.includes(id)
  }

  @computed get userInfo() {
    const {stores, log} = this, {users: {getById}} = stores,
      userInfo = getById(log.userId)

    return userInfo.name
  }

  @computed get fieldLookupKey() {
    const {field, workspace} = this
    //return `${workspace?.id ?? 'No Workspace'} - ${field?.field}`
    return field?.field
  }

  @computed get paletteIndex() {
    const {fieldLookupKey, root: {attributePalettes}} = this
    // console.log(toJS({fieldLookupKey, attributePalettes}))
    return attributePalettes[fieldLookupKey]
  }

  @computed get timestamp() {
    const {log: {timestamp}} = this

    return moment(timestamp).format('MM/DD/YYYY hh:mm A')
  }

  @computed get showBeforeAfterText() {
    return this.field?.def?.type !== 'Boolean'
  }

  @computed get fieldIcon() {
    const {field, changed: {diff}} = this

    if (field?.def?.type === 'Boolean') {
      return <Icon icon='tick'/>
    }

    return null
  }

  @computed get isRef() {
    return this.field?.esIndex != null
  }

  @action toggleFilterField = () => {
    const {field, root} = this
    root.onToggleFilterField(field.field)()
  }

  @computed get refWorkspace() {
    const {field, workspaces: {wrapperByIndex}} = this

    return field?.esIndex ? wrapperByIndex[field.esIndex] : null
  }
}

export const ChangeLogEntryIndividualStoreContext = React.createContext<ChangeLogEntryIndividualStore>(null)
export const useChangeLogEntryIndividualStore = () => React.useContext(ChangeLogEntryIndividualStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  item: ChangeLogEntryByField
  index: number
  includeTimestampAndUser?: boolean
}

export const ChangeLogEntryIndividual = observer<MyProps>(function ChangeLogEntryIndividual({className, includeTimestampAndUser, ...props}) {
  const parent = useChangeLogEntriesForCatalogItemAtTimestampStore(),
    obsProps = useAsObservableSource({...props, parent}),
    store = useVipLocalStore((stores) => new ChangeLogEntryIndividualStore(stores, obsProps)),
    {imageCountLabel, isRef, toggleFilterField, fieldIcon, fieldLabel, before, after, beforeRaw, afterRaw, field, userInfo, paletteIndex, showBeforeAfterText, timestamp, refWorkspace, showStrikethrough} = store

  useEffect(() => store.registerEffects(), [])

  if (!field) {
    // console.debug(`Unable to render changelog - unable to find field for`, {...props})
    return null
  }

  // console.log(`ChangeLogEntryIndividual`, props.item, {before, after, nested, label})
  return (
    <ChangeLogEntryIndividualStoreContext.Provider value={store}>
      <div
        className={classNames(css.root, className)}
        data-field-type={field?.def?.type}
        data-has-delta={showBeforeAfterText}
        data-before={before}
        data-after={after}
        data-show-strikethrough={showStrikethrough}>
        {imageCountLabel ?? <span
			data-type={'field-label'}
			onClick={toggleFilterField}
			data-palette-index={paletteIndex}>
          {fieldIcon}
          {fieldLabel}
        </span>}

        {showBeforeAfterText && <div className={css.deltaText}>
			<Popover2
				minimal={false}
				disabled={!isRef}
				interactionKind='hover'
				placement='bottom'
				content={
                  <BuilderCard
                    data={beforeRaw}
                    showImage
                    isPopover
                    workspace={refWorkspace?.config}
                    visibleCardFields={refWorkspace?.defaultVisibleCardFields}
                  />}
			>
				<span data-type='before-text'>{before}</span>
			</Popover2>
			<Popover2
				minimal={false}
				disabled={!isRef}
				placement='bottom'
				interactionKind='hover'
				content={
                  <BuilderCard
                    data={afterRaw}
                    showImage
                    isPopover
                    workspace={refWorkspace?.config}
                    visibleCardFields={refWorkspace?.defaultVisibleCardFields}
                  />
                }
			>
				<span data-type='after-text'>{after}</span>
			</Popover2>
		</div>}

        {includeTimestampAndUser && <Fragment>
			<Tooltip title={timestamp}>
				<TimeAgo className={css.timestamp} date={props.item.item._log.timestamp}/>
			</Tooltip>
			<span className={css.username}>({userInfo})</span>
		</Fragment>}
      </div>
    </ChangeLogEntryIndividualStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/changelog/entries/ChangeLogEntryIndividual.tsx